/**
 * Shapes of plan objects, computed straight from the document without caching.
 * Ops use these on immer drafts; derive() wraps them with a cache for committed docs.
 */
import type { PlanDoc, Pt, Road, RoadNode, Vert, VertId } from '../../shared/types';
import { chaikin, spline, spreadAlong } from './basic';

/** Chaikin passes for area and boundary rings (TD §4). */
export const CHAIKIN_ITERS = 2;

/** A road's centre line and its width at every sample. Node j of the road sits at sample j*SEG. */
export interface RoadPoly { pts: Pt[]; widths: number[] }

/** The corner objects of a ring, skipping ids that don't exist. */
export function ringVerts(doc: PlanDoc, ids: readonly VertId[]): Vert[] {
  const out: Vert[] = [];
  for (const id of ids) { const v = doc.verts[id]; if (v) out.push(v); }
  return out;
}

/** The raw corner positions of a ring. */
export function ringPoints(doc: PlanDoc, ids: readonly VertId[]): Pt[] {
  return ringVerts(doc, ids).map(v => [v.x, v.y]);
}

/** Smoothed ring from corner objects: Chaikin with pinned corners kept, or straight when `smooth` is false. */
export function smoothRing(verts: readonly Vert[], smooth = true): Pt[] {
  const pts: Pt[] = verts.map(v => [v.x, v.y]);
  if (!smooth || pts.length < 3) return pts;
  return chaikin(pts, verts.map(v => !!v.pinned), CHAIKIN_ITERS);
}

/** The road's node objects in order, skipping ids that don't exist. */
export function roadNodes(doc: PlanDoc, road: Road): RoadNode[] {
  const out: RoadNode[] = [];
  for (const id of road.nodes) { const n = doc.nodes[id]; if (n) out.push(n); }
  return out;
}

/**
 * Catmull-Rom centre line (SEG samples per span) through the node positions, with the width interpolated
 * linearly between nodes. A closed road repeats its first sample at the end, so it draws as a closed line.
 */
export function roadPolyFromNodes(nodes: readonly RoadNode[], closed: boolean): RoadPoly {
  const ctrl: Pt[] = nodes.map(n => [n.x, n.y]);
  if (ctrl.length < 2) return { pts: ctrl, widths: nodes.map(n => n.w) };
  const isClosed = closed && ctrl.length >= 3;
  const pts = spline(ctrl, isClosed);
  if (isClosed) { const f = pts[0] as Pt; pts.push([f[0], f[1]]); }
  return { pts, widths: spreadAlong(nodes.map(n => n.w), isClosed, pts.length) };
}

/** Centre line and widths of one road in the doc (or draft). */
export function roadPoly(doc: PlanDoc, road: Road): RoadPoly {
  return roadPolyFromNodes(roadNodes(doc, road), road.closed);
}

/** Centre line of a river. */
export function riverPoly(pts: readonly Pt[]): Pt[] {
  return spline(pts, false);
}
